import './SupportDiagnosticsPreview.scss';
import { CSSProperties, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseButton } from '@mantine/core';
import { diagnosticsFacts, type ISupportDiagnostics } from '@srm/screen-sharing/light';
import { usePortalTarget } from '@srm/ui/provider';

export interface ISupportDiagnosticsPreviewProps {
    diagnostics: ISupportDiagnostics;
    /** Точка под плашкой запроса: правый верхний угол панели — здесь */
    anchor: HTMLElement | null;
    /**
     * Сотрудник видит и эту панель: захват не ограничен корнем страницы (браузер без Element Capture или показ обрезан
     * до рабочей области) — предупреждение в панели
     */
    visibleToSupport?: boolean;
    onClose: () => void;
}

const GAP = 6;

/** Место панели в окне под `anchor`; следит за прокруткой, размером окна и сдвигом плашки. */
function useBelow(anchor: HTMLElement | null): CSSProperties | null {
    const [place, setPlace] = useState<{ top: number; right: number } | null>(null);
    useLayoutEffect(() => {
        if (!anchor) return undefined;
        const measure = () => {
            const rect = anchor.getBoundingClientRect();
            const next = { top: Math.round(rect.bottom + GAP), right: Math.round(window.innerWidth - rect.right) };
            setPlace((current) =>
                current && current.top === next.top && current.right === next.right ? current : next,
            );
        };
        measure();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        observer?.observe(document.body);
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [anchor]);
    return anchor && place ? { position: 'fixed', top: place.top, right: Math.max(12, place.right) } : null;
}

const time = (at: string) => {
    const date = new Date(at);
    return Number.isNaN(date.getTime())
        ? ''
        : date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

/**
 * «Посмотреть, что уйдёт» (IMPLEMENTATION.md §43): пакет диагностики до отправки — разделы читаемо, запросы и ошибки
 * страницы списками, внизу — тот же пакет как JSON. Уходит ровно показанное. Панель — в хосте порталов полосы (слой
 * оверлеев поддержки, вне захвата): до согласия сотрудник пакета не видит.
 */
const SupportDiagnosticsPreview = ({
    diagnostics,
    anchor,
    visibleToSupport,
    onClose,
}: ISupportDiagnosticsPreviewProps) => {
    const sections = useMemo(() => diagnosticsFacts(diagnostics), [diagnostics]);
    const json = useMemo(() => JSON.stringify(diagnostics, null, 2), [diagnostics]);
    const validation = diagnostics.document?.validation ?? [];
    const host = usePortalTarget();
    const place = useBelow(anchor);
    if (!place) return null;
    return createPortal(
        <section
            className="support-diagnostics-preview"
            aria-label="Технические сведения для поддержки"
            data-remote-control-exclude
            data-testid="screen-share-diagnostics-preview"
            style={place}
        >
            <header className="support-diagnostics-preview__header">
                <h4>Что уйдёт сотруднику</h4>
                <CloseButton size={24} iconSize={16} aria-label="Скрыть сведения" onClick={onClose} />
            </header>
            <p className="support-diagnostics-preview__note">
                Значений полей документа, паролей, cookie и содержимого запросов здесь нет.
            </p>
            {visibleToSupport && (
                <p className="support-diagnostics-preview__warning" role="note">
                    Сотрудник видит эту вкладку целиком — и эти сведения тоже.
                </p>
            )}
            {sections.map((section) => (
                <section key={section.section} className="support-diagnostics-preview__section">
                    <h5>{section.title}</h5>
                    <dl>
                        {section.facts.map((item) => (
                            <div key={item.label}>
                                <dt>{item.label}</dt>
                                <dd>{item.value}</dd>
                            </div>
                        ))}
                    </dl>
                    {section.section === 'document' && validation.length > 0 && (
                        <ul className="support-diagnostics-preview__list">
                            {validation.map((item, index) => (
                                <li key={`${item.key ?? item.label}:${index}`}>
                                    <b>{item.label}</b> — {item.message}
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            ))}
            <section className="support-diagnostics-preview__section">
                <h5>Последние запросы · {diagnostics.network.length}</h5>
                {diagnostics.network.length ? (
                    <ol className="support-diagnostics-preview__list support-diagnostics-preview__list--log">
                        {diagnostics.network.map((entry, index) => (
                            <li key={`${entry.at}:${index}`}>
                                <time dateTime={entry.at}>{time(entry.at)}</time>
                                <span>
                                    {entry.method} {entry.path}
                                </span>
                                <span>
                                    {entry.aborted ? 'отменён' : (entry.status ?? 'нет ответа')} · {entry.durationMs} мс
                                </span>
                            </li>
                        ))}
                    </ol>
                ) : (
                    <p className="support-diagnostics-preview__empty">Запросов не было</p>
                )}
            </section>
            <section className="support-diagnostics-preview__section">
                <h5>Ошибки страницы · {diagnostics.console.length}</h5>
                {diagnostics.console.length ? (
                    <ol className="support-diagnostics-preview__list">
                        {diagnostics.console.map((entry, index) => (
                            <li key={`${entry.at}:${index}`}>
                                <time dateTime={entry.at}>{time(entry.at)}</time> {entry.text}
                            </li>
                        ))}
                    </ol>
                ) : (
                    <p className="support-diagnostics-preview__empty">Ошибок не было</p>
                )}
            </section>
            <details className="support-diagnostics-preview__json">
                <summary>Как JSON</summary>
                <pre>{json}</pre>
            </details>
        </section>,
        host ?? document.body,
    );
};

export default SupportDiagnosticsPreview;
